import { useEffect, useMemo } from 'react';

export type KeyPlatform = 'mac' | 'windows' | 'linux';

export type KeyboardShortcut = {
  code: string;
  modifiers: KeyModifier[] | Record<KeyPlatform, KeyModifier[]>;
  preventDefault?: boolean;
  description?: string;
  disabled?: boolean;
};

export type KeyComboOptions = {
  preventDefault?: boolean;
  swapMetaCtrl?: boolean;
};

export type KeyComboWithCallback = KeyboardShortcut & {
  callback: (e: KeyboardEvent) => void;
};

function platformToKeyPlatform(platform: string): KeyPlatform {
  const lowerCasePlatform = platform.toLowerCase();
  if (lowerCasePlatform.includes('mac')) {
    return 'mac';
  }
  if (lowerCasePlatform.includes('win')) {
    return 'windows';
  }
  return 'linux';
}

function modifiersActive(event: KeyboardEvent, modifiers: KeyModifier[]) {
  return modifiers.every((modifier) => event[`${modifier}Key`]);
}

export function keyboardEventMatches(
  e: KeyboardEvent,
  combo: KeyboardShortcut
): boolean {
  const platform = platformToKeyPlatform(navigator.platform);
  if (e.code === combo.code) {
    if (isModifiersArray(combo.modifiers)) {
      if (modifiersActive(e, combo.modifiers)) {
        if (combo.disabled) {
          return false;
        }
        return true;
      }
      return false;
    } else {
      if (modifiersActive(e, combo.modifiers[platform])) {
        if (combo.disabled) {
          return false;
        }
        return true;
      }
      return false;
    }
  }
  return false;
}

export function registerShortcut(
  combo: KeyboardShortcut,
  callback: () => void
) {
  function handler(e: KeyboardEvent) {
    if (keyboardEventMatches(e, combo)) {
      if (combo.preventDefault) {
        e.preventDefault();
      }
      callback();
    }
  }
  document.addEventListener('keydown', (e) => {
    handler(e);
  });
}

export const useKeyboardShortcut = (
  combo: KeyboardShortcut,
  callback: () => void
) => {
  function handler(e: KeyboardEvent) {
    if (keyboardEventMatches(e, combo)) {
      if (combo.preventDefault) {
        e.preventDefault();
      }
      callback();
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [combo]);

  const shortcutText = useMemo(() => {
    const platform = platformToKeyPlatform(navigator.platform);
    const key = combo.code.replace('Key', '');
    let modifiers = '';
    if (isModifiersArray(combo.modifiers)) {
      modifiers = combo.modifiers.join(' + ');
    } else {
      modifiers = combo.modifiers[platform].join(' + ');
    }
    let text = `${modifiers}+${key}`.replace('meta', '⌘');
    if (combo.description) {
      text += ` - ${combo.description}`;
    }
    return text;
  }, [combo]);
  return [shortcutText, combo] as const;
};

function isModifiersArray(
  modifiers: KeyModifier[] | Record<KeyPlatform, KeyModifier[]>
): modifiers is KeyModifier[] {
  return Array.isArray(modifiers);
}

// export const useKeyCombos = (
// combos: KeyComboWithCallback[],
// options: KeyComboOptions = {}
// ) => {
//     const combosMap = useMemo(() => {
//         const map = new Map<string, KeyComboWithCallback>()
//         for (const combo of combos) {
//             map.set(combo.code, combo)
//         }
//         return map
//     }, [combos, options])
//
//     function handler(e: KeyboardEvent) {
//         const platform = platformToKeyPlatform(navigator.platform)
//         const activeCombo = keyboardEventToKeyCombo(e)
//         const combo = combosMap.get(activeCombo.code)
//         if (combo) {
//             if (combo.preventDefault || options.preventDefault) {
//                 e.preventDefault()
//             }
//             if (combo.platform) {
//                 if (combo.platform === platform) {
//                     combo.callback(e)
//                 }
//             } else {
//                 combo.callback(e)
//             }
//         }
//         return false
//     }
//
//     const comboMapText = useMemo(() => {
//         const platform = platformToKeyPlatform(navigator.platform)
//         const output: string[] = []
//         combosMap.forEach((combo) => {
//             const key = combo.code.replace('Key', '')
//             let text = `${combo.modifiers.join(' + ')}+${key}`.replace('meta', '⌘')
//             if (combo.description) {
//                 text += ` - ${combo.description}`
//             }
//             if (combo.platform) {
//                 if (combo.platform === platform) {
//                     output.push(text)
//                 }
//             } else {
//                 output.push(text)
//             }
//         })
//         return output
//     }, [combosMap])
//
//     useEffect(() => {
//         document.addEventListener('keydown', handler)
//         return () => document.removeEventListener('keydown', handler)
//     }, [combos])
//
//     return comboMapText
// }

export type KeyModifier = 'meta' | 'ctrl' | 'alt' | 'shift';

type KeyEventKey =
  | 'Backspace'
  | 'Tab'
  | 'Enter'
  | 'Shift'
  | 'Shift'
  | 'Control'
  | 'Control'
  | 'Alt'
  | 'Alt'
  | 'Pause'
  | 'CapsLock'
  | 'Escape'
  | ''
  | 'PageUp'
  | 'PageDown'
  | 'End'
  | 'Home'
  | 'ArrowLeft'
  | 'ArrowUp'
  | 'ArrowRight'
  | 'ArrowDown'
  | 'PrintScreen'
  | 'Insert'
  | 'Delete'
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r'
  | 's'
  | 't'
  | 'u'
  | 'v'
  | 'w'
  | 'x'
  | 'y'
  | 'z'
  | 'Meta'
  | 'Meta'
  | 'ContextMenu'
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '*'
  | '+'
  | '-'
  | '.'
  | '/'
  | 'F1'
  | 'F2'
  | 'F3'
  | 'F4'
  | 'F5'
  | 'F6'
  | 'F7'
  | 'F8'
  | 'F9'
  | 'F10'
  | 'F11'
  | 'F12'
  | 'NumLock'
  | 'ScrollLock'
  | 'AudioVolumeMute'
  | 'AudioVolumeDown'
  | 'AudioVolumeUp'
  | 'LaunchMediaPlayer'
  | 'LaunchApplication1'
  | 'LaunchApplication2'
  | ';'
  | '='
  | ','
  | '-'
  | '.'
  | '/'
  | '`'
  | '['
  | '\\'
  | ']';
